import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useGlobalUnreadListener } from '@avanta/shared/hooks/chat/useGlobalUnreadListener';
import { tExists } from '@avanta/shared/i18n';
import { cn } from '@avanta/shared/utils/cn';
import { getUserDisplayName } from '@avanta/shared/utils/users';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import { pathToNavKey } from 'constants/nav';
import { ROUTES } from 'constants/routes';
import { useRateAppointment } from 'hooks/useRateAppointment';
import { useUserStore } from 'stores/user';
import { Footer } from '../Footer';
import { TopNav } from '../TopNav';
import { Notifications } from './Notifications';

const HERO_LAYOUT_ROUTES = [ROUTES.HOME, ROUTES.PAE] as const;
const ROUTES_WITHOUT_HEADING: string[] = [...HERO_LAYOUT_ROUTES];

const LAYOUT_MAIN_CLASS = {
  hero: 'mx-auto w-full flex-1 px-0 py-0 max-am-lg:pb-4',
  default: 'container mx-auto max-w-7xl flex-1 px-4 py-8',
} as const;

const LAYOUT_OUTLET_CLASS = {
  hero: 'py-0',
  default: 'py-4 max-am-lg:pt-14',
} as const;

export const Layout = (): React.ReactElement => {
  const pathname = useLocation().pathname;
  const nickname = getUserDisplayName(useUserStore(state => state.user));
  const navKey = pathToNavKey(pathname);
  const isHeroRoute = HERO_LAYOUT_ROUTES.includes(pathname);
  const layoutVariant = isHeroRoute ? 'hero' : 'default';
  const showLayoutHeading =
    !ROUTES_WITHOUT_HEADING.includes(pathname) && !!navKey && tExists(`Nav.${navKey}`);

  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const pageTitle = showLayoutHeading ? t(`Nav.${navKey}`, { nickname }) : '';
  useRateAppointment();
  useGlobalUnreadListener();

  return (
    <div className={cn('flex min-h-screen flex-col', pathname === ROUTES.PAE && 'relative')}>
      {pathname === ROUTES.PAE && (
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 right-0 left-0 z-0 h-120 bg-mint-100 am-3xl:hidden"
        />
      )}
      <TopNav />
      <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden">
        <div className={LAYOUT_MAIN_CLASS[layoutVariant]}>
          {showLayoutHeading && (
            <h1 className="text-3xl font-semibold max-am-lg:hidden">{pageTitle}</h1>
          )}
          <div className={LAYOUT_OUTLET_CLASS[layoutVariant]}>
            <Outlet />
          </div>
        </div>
        <Footer />
        <Notifications />
      </div>
    </div>
  );
};
